/**
 * Amount utilities for handling minor units (integers) in multi-currency system.
 * Platform-agnostic — works in browser, Node.js, and React Native.
 *
 * Minor units are the smallest unit of a currency:
 * - USD/EUR: cents (2 decimal places) - $1.23 = 123 minor units
 * - JPY: yen (0 decimal places) - 1000 = 1000 minor units
 * - CZK: halere (2 decimal places) - 122.40 Kc = 12240 minor units
 */

import { FALLBACK_CURRENCIES_DATA } from '../config/currencies-fallback';

const minorUnitsMap = new Map<string, number>(
  FALLBACK_CURRENCIES_DATA.map(c => [c.code, c.minor_units])
);

// Custom currency symbol mapping (matches mobile app's AmountDisplay)
const CURRENCY_SYMBOLS: Record<string, string> = {
  CZK: 'Kč', USD: '$', EUR: '€', GBP: '£', JPY: '¥', CNY: '¥',
  CHF: 'CHF', CAD: 'C$', AUD: 'A$', NZD: 'NZ$', SEK: 'kr', NOK: 'kr',
  DKK: 'kr', PLN: 'zł', HUF: 'Ft', RON: 'lei', BGN: 'лв', HRK: 'kn',
  RUB: '₽', TRY: '₺', INR: '₹', BRL: 'R$', MXN: '$', ZAR: 'R',
  KRW: '₩', SGD: 'S$', HKD: 'HK$', THB: '฿', PHP: '₱', IDR: 'Rp',
  MYR: 'RM', VND: '₫',
};

// Currencies where symbol goes AFTER the number (e.g. "122,40 Kč")
const SYMBOL_AFTER_CURRENCIES = new Set([
  'CZK', 'SEK', 'NOK', 'DKK', 'PLN', 'HUF', 'RON', 'BGN', 'HRK',
]);

/**
 * Get minor units for a currency code (synchronous, from fallback data).
 * Defaults to 2 for unknown currencies.
 */
export function getMinorUnitsSync(currencyCode: string): number {
  return minorUnitsMap.get(currencyCode) ?? 2;
}

/**
 * Convert a decimal amount to minor units (integer).
 */
export function toMinorUnits(amount: number, minorUnits: number): number {
  if (!Number.isFinite(amount)) {
    throw new Error('Amount must be a finite number');
  }
  const factor = Math.pow(10, minorUnits);
  return Math.round(amount * factor);
}

/**
 * Convert minor units back to a decimal amount.
 */
export function fromMinorUnits(minorAmount: number, minorUnits: number): number {
  const factor = Math.pow(10, minorUnits);
  return minorAmount / factor;
}

/**
 * Get the browser locale, falling back to 'en-US'.
 */
export function getBrowserLocale(): string {
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }
  return 'en-US';
}

/**
 * Format minor units for display with proper localization.
 *
 * For known currencies (33 in CURRENCY_SYMBOLS), uses manual symbol placement
 * to match the mobile app — e.g. "122,40 Kč" instead of "CZK 122.40".
 * For unknown currencies, falls back to Intl currency formatting.
 */
export function formatMinorAmount(
  minorAmount: number,
  currencyCode: string,
  minorUnits: number,
  locale?: string,
  hideDecimals: boolean = false
): string {
  const amount = fromMinorUnits(minorAmount, minorUnits);
  const displayDecimals = hideDecimals ? 0 : minorUnits;
  const displayAmount = hideDecimals ? Math.round(amount) : amount;
  const effectiveLocale = locale ?? getBrowserLocale();

  const symbol = CURRENCY_SYMBOLS[currencyCode.toUpperCase()];

  if (symbol) {
    const formatted = new Intl.NumberFormat(effectiveLocale, {
      style: 'decimal',
      minimumFractionDigits: displayDecimals,
      maximumFractionDigits: displayDecimals,
    }).format(displayAmount);

    return SYMBOL_AFTER_CURRENCIES.has(currencyCode.toUpperCase())
      ? `${formatted}\u00A0${symbol}`
      : `${symbol}${formatted}`;
  }

  try {
    return new Intl.NumberFormat(effectiveLocale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: displayDecimals,
      maximumFractionDigits: displayDecimals,
    }).format(displayAmount);
  } catch {
    const formatted = new Intl.NumberFormat(effectiveLocale, {
      minimumFractionDigits: displayDecimals,
      maximumFractionDigits: displayDecimals,
    }).format(displayAmount);
    return `${formatted} ${currencyCode}`;
  }
}

/**
 * Format a regular number amount for display (convenience wrapper).
 */
export function formatAmount(
  amount: number,
  currencyCode: string,
  locale?: string,
  hideDecimals: boolean = false
): string {
  if (!Number.isFinite(amount)) {
    amount = 0;
  }
  const minorUnits = getMinorUnitsSync(currencyCode);
  const minorAmount = toMinorUnits(amount, minorUnits);
  return formatMinorAmount(minorAmount, currencyCode, minorUnits, locale, hideDecimals);
}

/**
 * Parse user input string to minor units.
 * Handles formats: "123.45", "123,45", "1,234.56", "1.234,56"
 */
export function parseToMinorUnits(input: string, minorUnits: number): number | null {
  if (!input || typeof input !== 'string') return null;

  let cleaned = input.replace(/\s/g, '').replace(/[^\d,.-]/g, '');
  if (!cleaned) return null;

  const isNegative = cleaned.startsWith('-');
  if (isNegative) cleaned = cleaned.substring(1);

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');

  let normalized: string;

  if (lastComma > -1 && lastDot > -1) {
    if (lastComma > lastDot) {
      normalized = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = cleaned.replace(/,/g, '');
    }
  } else if (lastComma > -1) {
    const parts = cleaned.split(',');
    if (parts.length === 2 && parts[1].length <= 3) {
      normalized = cleaned.replace(',', '.');
    } else {
      normalized = cleaned.replace(/,/g, '');
    }
  } else {
    normalized = cleaned;
  }

  const parsed = parseFloat(normalized);
  if (isNaN(parsed)) return null;

  const finalAmount = isNegative ? -parsed : parsed;
  return toMinorUnits(finalAmount, minorUnits);
}

/**
 * Convert amount between currencies using exchange rate.
 */
export function convertMinorUnits(
  minorAmount: number,
  rate: number,
  sourceMinorUnits: number,
  targetMinorUnits: number
): number {
  const sourceAmount = fromMinorUnits(minorAmount, sourceMinorUnits);
  const targetAmount = sourceAmount * rate;
  return toMinorUnits(targetAmount, targetMinorUnits);
}

/**
 * Split an amount equally among multiple parties.
 * Handles rounding by adjusting the last amounts.
 */
export function splitEqually(minorAmount: number, parts: number): number[] {
  if (parts <= 0 || !Number.isInteger(parts)) {
    throw new Error('Parts must be a positive integer');
  }

  const baseAmount = Math.floor(minorAmount / parts);
  const remainder = minorAmount - baseAmount * parts;

  const result: number[] = new Array(parts).fill(baseAmount);

  for (let i = 0; i < remainder; i++) {
    result[parts - 1 - i] += 1;
  }

  return result;
}

/**
 * Sum multiple minor amounts.
 */
export function sumMinorAmounts(amounts: number[]): number {
  return amounts.reduce((sum, amount) => sum + amount, 0);
}
